import Navbar from "../components/Navbar";
import PageHero from "../components/PageHero";
import Stats from "../components/Stats";
import About from "../components/About";
import Process from "../components/Process";
import Gallery from "../components/Gallery";
import CuratedReviews from "../components/CuratedReviews";
import FAQ from "../components/FAQ";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import {
  SEO,
  localBusinessSchema,
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
} from "../components/SEO";
import { BRAND } from "../mock";

const FAQS = [
  {
    q: "How much does paint correction cost in Mississauga?",
    a: "At Jester Werks, paint correction starts at $275 for a 1-stage enhancement. Pricing scales by vehicle size, paint condition and the number of correction stages required (1, 2 or 3-stage). Our flagship 2-layer ceramic coating package already includes a 1-stage paint correction at $550. We always provide a written quote within 24 hours.",
  },
  {
    q: "What is paint correction?",
    a: "Paint correction is a multi-stage machine polishing process that permanently removes swirl marks, scratches, oxidation and other paint defects. restoring your finish to better-than-factory gloss. It is the foundation of any quality ceramic coating job and is the only way to truly fix paint defects (versus filler products that hide them temporarily).",
  },
  {
    q: "Will paint correction damage my paint?",
    a: "No. when done correctly. We measure paint depth before, during and after each stage with digital paint depth gauges to ensure we never remove more clear coat than necessary. Every job in our Mississauga studio is documented with paint depth readings and photographs under swirl-finder lighting.",
  },
  {
    q: "How long does paint correction take?",
    a: "A 1-stage enhancement typically takes 1 day, a 2-stage correction takes 1.5 to 2 days, and a full 3-stage correction can take 2 to 3 days depending on vehicle size. We never rush. quality work cannot be rushed.",
  },
  {
    q: "What is the difference between 1, 2 and 3-stage paint correction?",
    a: "A 1-stage enhancement removes light swirl marks and adds gloss; a 2-stage correction removes deeper scratches and oxidation; a 3-stage correction is for severely damaged paint requiring heavy compounding, intermediate polishing and finishing. We recommend the right stage after a free in-person inspection.",
  },
  {
    q: "Do you do paint correction without ceramic coating?",
    a: "Yes. paint correction can be booked as a standalone service in our Mississauga studio. However, we strongly recommend protecting freshly corrected paint with at least our 2-layer ceramic coating to lock in the gloss and prevent re-marring during washes. Otherwise the swirls will start coming back within months.",
  },
  {
    q: "Where do you do paint correction in Mississauga?",
    a: "Our Mississauga studio (L5M 7W4) handles paint correction for clients across Mississauga. Port Credit, Streetsville, Erin Mills, Meadowvale, Cooksville, Lorne Park, Square One, Erindale and beyond. plus Toronto, Oakville, Brampton, Etobicoke, Burlington and the entire GTA.",
  },
  {
    q: "Will paint correction remove deep scratches?",
    a: "Paint correction can remove or significantly reduce most scratches that have not penetrated past the clear coat. Deep scratches that have reached the basecoat or primer require touch-up paint or panel respraying, which is outside the scope of correction. We will tell you honestly during the inspection what is correctable.",
  },
];

export default function PaintCorrectionPage() {
  const url = `${BRAND.url}/paint-correction-mississauga`;
  return (
    <div className="grain bg-jester-deep text-white">
      <SEO
        title="Paint Correction Mississauga | 1, 2 & 3-Stage Polishing | Jester Werks"
        description="Professional paint correction Mississauga from $275. Multi-stage hand-led machine polishing erases swirls, scratches & oxidation. with measured paint depth on every panel. 250+ verified Google reviews. Mississauga's most-reviewed studio. Get a free quote: (647) 646-7223."
        canonical={url}
        schemas={[
          localBusinessSchema(),
          serviceSchema({
            name: "Paint Correction Mississauga",
            description:
              "Professional paint correction service in Mississauga. 1, 2 and 3-stage hand-led machine polishing to remove swirl marks, scratches and oxidation. restoring factory-deep gloss. Paint depth measured before, during and after every job.",
            areaServed: [
              "Mississauga", "Port Credit", "Streetsville", "Erin Mills",
              "Square One", "Erindale", "Toronto", "Oakville", "Brampton", "Etobicoke",
            ],
            offers: "275",
          }),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", url: `${BRAND.url}/` },
            { name: "Paint Correction Mississauga", url },
          ]),
        ]}
      />
      <Navbar />
      <PageHero
        kicker="Mississauga · Multi-Stage · Measured"
        h1="PAINT"
        h1Accent="CORRECTION."
        marker="every swirl, gone."
        description="Hand-led, multi-stage machine polishing in Mississauga that permanently erases swirl marks, scratches and oxidation. bringing back factory-deep gloss with measured paint depth on every panel. From $275."
        city="Mississauga"
        bgImage="https://images.pexels.com/photos/14231684/pexels-photo-14231684.jpeg"
      />

      <section className="relative py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-acid mb-5 font-label">
           The Process
          </div>
          <h2 className="font-display text-4xl md:text-5xl leading-[1] mb-8">
            <span className="text-jester">Paint correction</span> done
            properly. Measured, photographed, perfected.
          </h2>
          <div className="text-white/75 text-lg leading-relaxed space-y-5">
            <p>
              <strong className="text-white">
                Paint correction in Mississauga
              </strong>{" "}
              is not just polishing. it is a multi-step process of
              measurement, decontamination and graduated machine compounding
              that <strong className="text-white">permanently eliminates
              defects</strong> without removing more clear coat than
              necessary. Most "detailing" shops in Mississauga and the GTA
              skip the measurement step entirely. We don't.
            </p>
            <p>
              Every paint correction job at Jester Werks starts with a free
              in-person inspection under swirl-finder LED lighting. We
              photograph every defect, measure clear-coat thickness with a
              digital paint depth gauge, and design a correction plan
              specifically for your vehicle. You receive a written quote
              before any work begins.
            </p>
            <p>
              We offer three correction levels:{" "}
              <strong className="text-white">1-stage enhancement</strong>{" "}
              (removes light swirls and adds gloss),{" "}
              <strong className="text-white">2-stage correction</strong>{" "}
              (removes deeper scratches and oxidation), and{" "}
              <strong className="text-white">3-stage correction</strong>{" "}
              (heavy compound, intermediate polish and finishing. for
              severely neglected paint). Pricing starts at{" "}
              <strong className="text-acid">$275</strong> for a 1-stage and
              scales up based on vehicle size and paint condition.
            </p>
            <p>
              Throughout the correction we re-measure paint depth at multiple
              points on every panel, take progress photos under controlled
              lighting, and never remove more clear coat than necessary. The
              result is a permanent fix that holds up for years. not a
              filler-based "polish" that hides swirls until your next wash.
            </p>
            <p>
              Paint correction is the foundation that gives a{" "}
              <a href="/ceramic-coating-mississauga" className="text-acid underline underline-offset-4 hover:text-white">
                ceramic coating
              </a>{" "}
              its mirror-deep look for years. That's why our flagship $550
              ceramic coating package already includes a 1-stage correction.
              For deeper damage, we recommend booking a 2 or 3-stage
              correction first, followed by ceramic.
            </p>
            <p>
              We serve every Mississauga neighbourhood.{" "}
              <strong className="text-white">
                Port Credit, Streetsville, Erin Mills, Meadowvale, Cooksville,
                Lorne Park, Clarkson, Square One, Erindale, Heartland,
                Churchill Meadows, Hurontario, Applewood, Lakeview, Mineola,
                Sheridan, Malton
              </strong>{" "}
              and the Mississauga Valleys. plus Toronto, Etobicoke, Oakville,
              Brampton, Burlington, Vaughan and the broader GTA. Whether you
              searched <em className="text-white/90">"paint correction
              Mississauga"</em>, <em className="text-white/90">"swirl removal
              near me"</em>, <em className="text-white/90">"machine polishing
              Mississauga"</em> or <em className="text-white/90">"scratch
              removal car"</em>, you've landed on Mississauga's most-reviewed
              correction studio. Call (647) 646-7223 to book your free
              inspection.
            </p>
          </div>
        </div>
      </section>

      <Stats />
      <About />
      <Process />
      <Gallery />
      <CuratedReviews />
      <FAQ items={FAQS} />
      <Contact />
      <Footer />
    </div>
  );
}
